import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Floating AI Service Agent widget (bottom-right).
 * Ek session ID localStorage me stored rehti hai so returning users
 * ka chat context Kimi ke paas restore ho jaata hai.
 *
 * Every conversation runs against POST /api/service-agent/chat which:
 *   • Talks to Kimi (Moonshot AI) via OpenAI-compatible SDK
 *   • Looks up customer installations from Mongo
 *   • Creates service tickets when needed
 *
 * Admin kill-switch: reads `service_agent_enabled` from /api/settings —
 * if false, widget stays hidden entirely.
 */
export default function ServiceAgentWidget() {
  const [enabled, setEnabled] = useState(null);   // null = still loading
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [
    {
      role: "assistant",
      content:
        "Namaste! Main **SIV AI** hun — Smart Infovision ki sales & service partner. Digital board price, EMI options, free demo, ya order status — jo bhi chahiye, turant batao! 🚀",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(
    () => localStorage.getItem("si_service_session") || "",
  );
  const scrollRef = useRef(null);

  // Check admin kill-switch on mount. Default to enabled if the settings
  // endpoint is unreachable so we don't accidentally hide the widget on
  // a network blip.
  useEffect(() => {
    api.get("/settings")
      .then((r) => setEnabled(r.data?.service_agent_enabled !== false))
      .catch(() => setEnabled(true));
  }, []);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setSending(true);
    try {
      // Streaming: use raw fetch since axios has no SSE helper. Backend
      // sends `data: {json}\n\n` frames — we accumulate delta.content
      // into the last assistant bubble live.
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/service-agent/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId || undefined, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let firstToken = true;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Split on complete `data: {...}\n\n` frames
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const f of frames) {
          const line = f.trim();
          if (!line.startsWith("data:")) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;
          try {
            const evt = JSON.parse(jsonStr);
            if (evt.session_id && evt.session_id !== sessionId) {
              setSessionId(evt.session_id);
              localStorage.setItem("si_service_session", evt.session_id);
            }
            if (evt.delta) {
              if (firstToken) { setSending(false); firstToken = false; }
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: (copy[copy.length - 1].content || "") + evt.delta };
                return copy;
              });
            }
          } catch (_) { /* ignore malformed frame */ }
        }
      }
    } catch (e) {
      setMessages((prev) => {
        const copy = [...prev];
        // Replace the empty assistant bubble with error text
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Sorry, connection dikkat aa gayi. WhatsApp par baat karein: +91-8527926648",
        };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Kill-switch: admin ne band kiya to widget hidden */}
      {enabled === false ? null : (
        <>
      {/* Floating bubble */}
      <AnimatePresence>
        {!open && enabled && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            onClick={() => setOpen(true)}
            className="fixed bottom-6 right-6 z-[60] group"
            data-testid="ai-agent-fab"
            aria-label="Open AI Service Chat"
          >
            <div className="relative">
              {/* Ambient pulse ring */}
              <span className="absolute inset-0 rounded-full bg-emerald-400/30 animate-ping" />
              <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white shadow-[0_10px_30px_-5px_rgba(6,95,70,0.5)] hover:shadow-[0_15px_40px_-5px_rgba(6,95,70,0.7)] hover:scale-105 active:scale-95 transition-all flex items-center justify-center">
                <Sparkles size={26} />
                {/* Red notification dot */}
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-orange-500 rounded-full ring-2 ring-white flex items-center justify-center">
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                </span>
              </div>
              {/* Hover tooltip */}
              <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-slate-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
                Chat with SIV AI
              </span>
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-[60] w-[92vw] max-w-[400px] h-[75vh] max-h-[620px] rounded-[28px] bg-white/95 backdrop-blur-xl shadow-[0_20px_60px_-15px_rgba(2,44,34,0.35)] border border-emerald-100 flex flex-col overflow-hidden"
            data-testid="ai-agent-panel"
          >
            {/* Header — layered gradient with grain overlay */}
            <div className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700" />
              <div className="absolute inset-0 opacity-20"
                   style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0%, transparent 40%)" }} />
              <div className="relative text-white px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-11 h-11 rounded-2xl bg-white/25 backdrop-blur-md ring-1 ring-white/40 flex items-center justify-center shadow-lg">
                      <Sparkles size={20} />
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-400 ring-2 ring-emerald-600" />
                  </div>
                  <div>
                    <p className="font-bold text-[15px] leading-tight tracking-tight" style={{ fontFamily: "Outfit" }}>
                      SIV AI
                    </p>
                    <p className="text-[10.5px] opacity-95 flex items-center gap-1.5 mt-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                      AI Assistant · Instantly responds
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="w-8 h-8 rounded-full hover:bg-white/15 active:bg-white/25 flex items-center justify-center transition"
                  data-testid="ai-agent-close"
                  aria-label="Close chat"
                >
                  <X size={17} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3.5 bg-gradient-to-b from-emerald-50/30 via-white to-white"
              data-testid="ai-agent-messages"
            >
              {messages.map((m, i) => {
                // Skip rendering an empty streaming bubble — the typing
                // indicator below fills its role until the first token.
                if (m.role === "assistant" && !m.content && i === messages.length - 1) return null;
                return <MessageBubble key={i} role={m.role} content={m.content} />;
              })}
              {sending && (
                <div className="flex items-center gap-2 pl-1">
                  <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <Sparkles size={12} className="text-white" />
                  </div>
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-3 py-2.5 shadow-sm">
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-bounce" />
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: "0.15s" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: "0.3s" }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quick actions */}
            {messages.length <= 1 && !sending && (
              <div className="px-4 pb-2.5 flex flex-wrap gap-1.5">
                {[
                  { label: "💰 Pricing", q: "Studio setup package ki pricing kya hai?" },
                  { label: "📍 Center", q: "Sabse paas ka experience center kahan hai?" },
                  { label: "🔧 Support", q: "Mera board sahi kaam nahi kar raha" },
                  { label: "🎬 Demo", q: "Free demo book karna hai" },
                ].map((s) => (
                  <button
                    key={s.label}
                    onClick={() => { setInput(s.q); }}
                    className="text-[11.5px] font-medium px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400 active:scale-95 transition"
                    data-testid={`ai-quick-${s.label.replace(/[^a-z]/gi, "").toLowerCase()}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {/* Input — pill-shaped, premium feel */}
            <div className="border-t border-emerald-100 bg-white/80 backdrop-blur-sm px-3 py-3 flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Apna message likhein…"
                disabled={sending}
                className="flex-1 resize-none max-h-28 px-4 py-2.5 text-sm rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none transition disabled:opacity-60"
                data-testid="ai-agent-input"
              />
              <button
                onClick={send}
                disabled={!input.trim() || sending}
                className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white hover:shadow-lg hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-none transition-all flex items-center justify-center flex-shrink-0 shadow-md"
                data-testid="ai-agent-send"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </div>
            <div className="text-center pb-2 text-[10px] text-slate-400 tracking-wide">
              Powered by SIV AI · Instant Replies · Secure
            </div>
          </motion.div>
        )}
      </AnimatePresence>
        </>
      )}
    </>
  );
}

function MessageBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex items-end gap-2 ${isUser ? "justify-end" : "justify-start"}`}
    >
      {!isUser && (
        <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex-shrink-0 flex items-center justify-center shadow-sm">
          <Sparkles size={12} className="text-white" />
        </div>
      )}
      <div
        className={`max-w-[80%] px-3.5 py-2.5 text-[13.5px] leading-[1.55] ${
          isUser
            ? "bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-[18px] rounded-br-[6px] shadow-sm whitespace-pre-wrap"
            : "bg-white border border-slate-200 text-slate-800 rounded-[18px] rounded-bl-[6px] shadow-sm siv-msg"
        }`}
      >
        {isUser ? content : <FormattedMessage text={content} />}
      </div>
    </motion.div>
  );
}

/**
 * Lightweight markdown renderer for chat replies.
 * Supports: **bold**, *italic*, `code`, `- ` / `* ` bullets, blank-line
 * paragraphs, and https:// autolinks. Deliberately avoids a full parser
 * so streaming half-tokens still look clean.
 */
function FormattedMessage({ text }) {
  if (!text) return null;
  // Split by blank lines into blocks
  const blocks = text.replace(/\r\n/g, "\n").split(/\n{2,}/);
  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block.split("\n");
        const isList = lines.every((l) => /^\s*[*\-•]\s+/.test(l));
        if (isList) {
          return (
            <ul key={bi} className="list-disc pl-4 space-y-1 my-1">
              {lines.map((l, li) => (
                <li key={li}>{renderInline(l.replace(/^\s*[*\-•]\s+/, ""))}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={bi} className={bi === 0 ? "" : "mt-2"}>
            {lines.map((l, li) => (
              <span key={li}>
                {renderInline(l)}
                {li < lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </>
  );
}

function renderInline(text) {
  // Order matters: bold before italic, and skip italic-inside-bold.
  const tokens = [];
  const regex = /(\*\*[^*]+?\*\*|`[^`]+?`|\bhttps?:\/\/\S+|\*[^*\s][^*]*?\*)/g;
  let last = 0;
  let m;
  let idx = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) tokens.push({ t: text.slice(last, m.index), k: "t", i: idx++ });
    const raw = m[0];
    if (raw.startsWith("**")) tokens.push({ t: raw.slice(2, -2), k: "b", i: idx++ });
    else if (raw.startsWith("`")) tokens.push({ t: raw.slice(1, -1), k: "c", i: idx++ });
    else if (raw.startsWith("http")) tokens.push({ t: raw, k: "l", i: idx++ });
    else if (raw.startsWith("*")) tokens.push({ t: raw.slice(1, -1), k: "i", i: idx++ });
    last = m.index + raw.length;
  }
  if (last < text.length) tokens.push({ t: text.slice(last), k: "t", i: idx++ });
  return tokens.map((tok) => {
    if (tok.k === "b") return <strong key={tok.i} className="font-semibold text-slate-900">{tok.t}</strong>;
    if (tok.k === "i") return <em key={tok.i}>{tok.t}</em>;
    if (tok.k === "c") return <code key={tok.i} className="px-1 py-0.5 rounded bg-slate-100 text-[12px] font-mono">{tok.t}</code>;
    if (tok.k === "l") return <a key={tok.i} href={tok.t} target="_blank" rel="noreferrer" className="text-emerald-600 underline hover:text-emerald-700">{tok.t}</a>;
    return <span key={tok.i}>{tok.t}</span>;
  });
}
